// NC1 signup bootstrap — "identity born at website signup" (AWS-PORT.md
// chunk 1).
//
// Called from Login.onAuthentication (the universal post-auth funnel, so
// email/password AND Google/hosted-UI signups take the same path — no OAuth
// special-case). For a brand-new account it:
//   1. probes GET /e2ee/account (also yields the altAccountId),
//   2. generates the identity (X25519 + Ed25519) and the epoch-0 ADK,
//   3. wraps the secrets under a fresh 160-bit recovery code (always) and,
//      where the platform supports WebAuthn PRF, under a new passkey too,
//   4. publishes the ONE-transaction bootstrap (bundle + genesis device list
//      + owner-only member list + sealed ADK + genesis log triplet),
//   5. caches the unwrapped identity in the IndexedDB keystore,
//   6. returns the recovery code for the require-acknowledge dialog.
//
// NEVER blocks signup: any failure sets a retry marker and the next login
// retries. Existing accounts without the marker are untouched (Virgil
// accounts keep working exactly as before — the coexistence prime directive).

import E2eeApi from '@/aws/e2ee/E2eeApi'
import { getSodium } from './sodium'
import * as Keystore from './Keystore'
import { prfProbablySupported, generatePrfSaltB64 } from './PasskeyPrf'
import { passkeyMode } from './PasskeyPolicy'
import { wrapSecrets, wrapToWebauthn } from './shared/kek.js'
import { generateRecoveryCode, recoveryCodeDisplay } from './shared/recovery.js'
import { b64, ub64, deviceListSignedBytes } from './shared/envelope.js'
import { canonicalizeBytes } from './shared/jcs.js'
import {
  KIND_IDENTITY, KIND_DEVICE_LIST, KIND_MEMBER_LIST,
  docHashIdentity, docHashDeviceList, docHashMemberList,
  signEntry, entryHash,
} from './shared/translog.js'

const pendingKey = (email) => `nc1.bootstrap.pending.${email}`

// memberList signed bytes — mirror of the dev-server signMemberList preimage.
function memberListSignedBytes(list) {
  return canonicalizeBytes({
    accountId: list.accountId,
    version: list.version,
    keyEpoch: list.keyEpoch,
    members: list.members,
  })
}

function buildBootstrap(sodium, { email, altAccountId, wraps, idSign, idEnc, adk }) {
  const ts = Date.now()

  const identityBundle = {
    email,
    userId: altAccountId,
    identityVersion: 1,
    idEncPk: b64(sodium, idEnc.publicKey),
    idSignPk: b64(sodium, idSign.publicKey),
    wraps,
  }

  // Genesis device list is EMPTY: the web is an identity holder, never an
  // upload device. Windows machines join in later chunks via device-transfer.
  const deviceList = { userId: altAccountId, version: 1, devices: [] }
  deviceList.identitySig = b64(sodium,
    sodium.crypto_sign_detached(deviceListSignedBytes(deviceList), idSign.privateKey))

  const memberList = {
    accountId: altAccountId,
    version: 1,
    keyEpoch: 0,
    members: [{
      userId: altAccountId,
      email,
      idEncPk: identityBundle.idEncPk,
      idSignPk: identityBundle.idSignPk,
      role: 'owner',
      joinedAt: ts,
    }],
  }
  memberList.ownerSig = b64(sodium,
    sodium.crypto_sign_detached(memberListSignedBytes(memberList), idSign.privateKey))

  const adkEnvelopes = [{
    accountId: altAccountId,
    keyEpoch: 0,
    userId: altAccountId,
    sealedAdk: b64(sodium, sodium.crypto_box_seal(adk, idEnc.publicKey)),
  }]

  // Genesis log triplet (lateGenesis omitted — fresh account at signup),
  // mirroring shared/translog.js commitDocs.
  const log = []
  let seq = 0
  let prevHash = ''
  const push = (core, newSignerSk = null) => {
    const entry = signEntry(sodium,
      { accountId: altAccountId, seq, prevHash, ts, ...core }, idSign.privateKey, newSignerSk)
    log.push(entry)
    prevHash = entryHash(sodium, entry)
    seq += 1
  }
  push({
    kind: KIND_IDENTITY, docVersion: 1,
    docHash: docHashIdentity(sodium, identityBundle),
    signerPk: identityBundle.idSignPk, newSignerPk: identityBundle.idSignPk,
  }, idSign.privateKey)
  push({
    kind: KIND_DEVICE_LIST, docVersion: 1,
    docHash: docHashDeviceList(sodium, deviceList),
    signerPk: identityBundle.idSignPk,
  })
  push({
    kind: KIND_MEMBER_LIST, docVersion: 1,
    docHash: docHashMemberList(sodium, memberList),
    signerPk: identityBundle.idSignPk,
  })

  return { email, identityBundle, deviceList, memberList, adkEnvelopes, log }
}

/**
 * Run the signup bootstrap when appropriate. Returns one of:
 *   { status: 'skipped' }  — not configured / not a new account / no marker
 *   { status: 'exists' }   — account already has an identity
 *   { status: 'created', recoveryCode, recoveryCodeGrouped, passkeyRegistered }
 *   { status: 'failed' }   — logged, marker set, retried at next login
 * Never throws, never blocks signup.
 *
 * `acquirePasskey({ email, required, prfSaltB64 })` is the UI hook (the
 * consent dialog): it returns { credentialIdB64, prfOutput } after a
 * successful ceremony, or null when skipped.
 */
export async function ensureSignupBootstrap({ email, isNewAccount, acquirePasskey }) {
  try {
    if (!E2eeApi.isConfigured()) {
      return { status: 'skipped' }
    }
    if (!isNewAccount && !localStorage.getItem(pendingKey(email))) {
      return { status: 'skipped' }
    }

    const probe = (await E2eeApi.getAccount(email)).data
    if (probe.hasIdentity) {
      localStorage.removeItem(pendingKey(email))
      return { status: 'exists' }
    }
    if (!probe.altAccountId) {
      throw new Error('account has no altAccountId')
    }
    const altAccountId = probe.altAccountId

    const sodium = await getSodium()

    const idSign = sodium.crypto_sign_keypair()
    const idEnc = sodium.crypto_box_keypair()
    const adk = sodium.randombytes_buf(32)
    const secrets = {
      id_enc_pk: b64(sodium, idEnc.publicKey),
      id_enc_sk: b64(sodium, idEnc.privateKey),
      id_sign_pk: b64(sodium, idSign.publicKey),
      id_sign_sk: b64(sodium, idSign.privateKey),
      identity_version: 1,
    }
    const secretsBytes = new TextEncoder().encode(JSON.stringify(secrets))

    // Recovery code — always. This is the guaranteed backstop.
    const recoveryCode = generateRecoveryCode(sodium)
    const wraps = { recovery: wrapSecrets(sodium, recoveryCode, secretsBytes) }

    // Passkey — where the platform supports PRF, governed by
    // VUE_APP_E2EE_PASSKEY_MODE (PasskeyPolicy). The WebAuthn ceremony runs
    // inside the consent dialog's click handler (a real user gesture);
    // `acquirePasskey` resolves with the material, or null when the user
    // skipped (optional mode). Failure still degrades to recovery-code-only —
    // in `required` mode the login catch-up (ensurePasskeyEnrollment) keeps
    // demanding enrollment until it succeeds.
    let passkeyRegistered = false
    try {
      if (passkeyMode() !== 'off' && acquirePasskey && await prfProbablySupported()) {
        const prfSaltB64 = generatePrfSaltB64()
        const material = await acquirePasskey({
          email,
          required: passkeyMode() === 'required',
          prfSaltB64,
        })
        if (material) {
          wraps.webauthn = [wrapToWebauthn(sodium, material.prfOutput, secretsBytes, {
            credentialId: material.credentialIdB64,
            prfSalt: prfSaltB64,
          })]
          sodium.memzero(material.prfOutput)
          passkeyRegistered = true
          // Remember that THIS browser minted the credential (UI hint only).
          await Keystore.recordLocalPasskey(email, material.credentialIdB64)
        }
      }
    } catch (e) {
      console.log('passkey registration skipped:', e && e.message)
    }

    const payload = buildBootstrap(sodium, { email, altAccountId, wraps, idSign, idEnc, adk })
    await E2eeApi.bootstrapAccount(payload)

    await Keystore.saveIdentity(email, { secrets, altAccountId, email })
    localStorage.removeItem(pendingKey(email))

    console.log(`nc1 bootstrap complete for ${email} (passkey=${passkeyRegistered})`)
    return {
      status: 'created',
      recoveryCode,
      recoveryCodeGrouped: recoveryCodeDisplay(recoveryCode),
      passkeyRegistered,
    }
  } catch (e) {
    console.log('nc1 bootstrap failed (will retry at next login)')
    console.log(e)
    try {
      localStorage.setItem(pendingKey(email), String(Date.now()))
    } catch { /* storage unavailable — retry naturally on a later login */ }
    return { status: 'failed' }
  }
}

/**
 * Add a webauthn wrap to the account's identity bundle, signed by the cached
 * identity — the shared core of the required-mode login catch-up AND the
 * post-link enrollment (a freshly linked browser registering its own
 * passkey). `requireAbsent: true` keeps catch-up semantics (skip when ANY
 * webauthn wrap exists); false appends alongside existing wraps. The PRF
 * salt is FIXED PER ACCOUNT (unlock evaluates the PRF before knowing which
 * credential answers), so an existing wrap's salt is reused. Throws on hard
 * failure; returns { status: 'exists' | 'deferred' | 'enrolled' }.
 */
export async function enrollPasskeyWrap({ email, cached, acquirePasskey, required, requireAbsent }) {
  const bundle = (await E2eeApi.getIdentityBundle(email)).data
  const existing = (bundle.wraps && bundle.wraps.webauthn) || []
  if (requireAbsent && existing.length) {
    return { status: 'exists' }
  }

  const prfSaltB64 = existing.length ? existing[0].prfSalt : generatePrfSaltB64()
  const material = await acquirePasskey({ email, required: !!required, prfSaltB64 })
  if (!material) {
    return { status: 'deferred' }
  }

  const sodium = await getSodium()
  const secretsBytes = new TextEncoder().encode(JSON.stringify(cached.secrets))
  const value = {
    ...bundle,
    wraps: {
      ...bundle.wraps,
      webauthn: [...existing, wrapToWebauthn(sodium, material.prfOutput, secretsBytes, {
        credentialId: material.credentialIdB64,
        prfSalt: prfSaltB64,
      })],
    },
  }
  sodium.memzero(material.prfOutput)

  const sig = b64(sodium, sodium.crypto_sign_detached(
    canonicalizeBytes(value), ub64(sodium, cached.secrets.id_sign_sk)))
  await E2eeApi.putIdentityBundle(email, value, sig)
  // Remember that THIS browser minted the credential (UI hint only).
  await Keystore.recordLocalPasskey(email, material.credentialIdB64)
  return { status: 'enrolled' }
}

/**
 * Required-mode login catch-up: once VUE_APP_E2EE_PASSKEY_MODE flips to
 * `required`, accounts that deferred enrollment (identity bundle has no
 * webauthn wrap) must register a passkey at login. Only possible where this
 * browser holds the cached identity (we can't wrap secrets we don't have —
 * keyless browsers pick this up after unlock in a later chunk). Publishes
 * the added wrap through the signature-checked identityBundle write.
 * Returns { status: 'skipped' | 'exists' | 'unsupported' | 'enrolled' |
 * 'deferred' | 'failed' }. Never throws, never blocks login.
 */
export async function ensurePasskeyEnrollment({ email, acquirePasskey }) {
  try {
    if (passkeyMode() !== 'required' || !acquirePasskey || !E2eeApi.isConfigured()) {
      return { status: 'skipped' }
    }
    const cached = await Keystore.loadIdentity(email)
    if (!cached || !cached.secrets) {
      return { status: 'skipped' }
    }
    if (!(await prfProbablySupported())) {
      return { status: 'unsupported' }
    }

    const result = await enrollPasskeyWrap({
      email, cached, acquirePasskey, required: true, requireAbsent: true,
    })
    if (result.status === 'enrolled') {
      console.log(`nc1 passkey catch-up complete for ${email}`)
    }
    return result
  } catch (e) {
    console.log('nc1 passkey catch-up failed (will retry at next login)')
    console.log(e)
    return { status: 'failed' }
  }
}
